import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import maintenanceProviderMasterService from '../../services/maintenanceProviderMasterService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  providerCode: '',
  providerName: '',
  providerDescription: '',
  contactName: '',
  email: '',
  phone: '',
  specialty: '',
  status: 'Active',
  notes: ''
}

const MaintenanceProviderMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const providerData = location.state?.providerData
  const recordId = providerData?._id || searchParams.get('id')

  const [formData, setFormData] = useState(emptyForm)
  const [statusOptions, setStatusOptions] = useState(
    () => optionsFromDropdown(null, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus)
  )
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        const response = await assetDropdownService.getOptions()
        if (response.success && response.data) {
          setStatusOptions(optionsFromDropdown(response.data.dropdowns, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus))
        }
      } catch (error) {
        console.error('Error loading provider dropdown options:', error)
      }
    }
    loadDropdownOptions()
  }, [])

  // Fetch next provider code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchProviderCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await maintenanceProviderMasterService.getNextProviderCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, providerCode: response.data.providerCode || 'MPRV0001' }))
          }
        } catch (error) {
          console.error('Error fetching provider code:', error)
          setFormData(prev => ({ ...prev, providerCode: 'MPRV0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchProviderCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  // Load record data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadProviderData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadProviderData = async (id) => {
    try {
      setIsLoading(true)
      const response = await maintenanceProviderMasterService.getProviderById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          providerCode: record.providerCode || '',
          providerName: record.providerName || '',
          providerDescription: record.providerDescription || '',
          contactName: record.contactName || '',
          email: record.email || '',
          phone: record.phone || '',
          specialty: record.specialty || '',
          status: record.status || 'Active',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading maintenance provider data:', error)
      toast.error('Failed to load maintenance provider data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    try {
      if (!formData.providerName) {
        toast.error('Please fill in Provider Name')
        return
      }

      setIsLoading(true)

      if (isEditMode && recordId) {
        const response = await maintenanceProviderMasterService.updateProvider(recordId, formData)
        if (response.success) {
          toast.success('Maintenance provider updated successfully!')
          navigate('/assets/maintenance-provider-master')
        } else {
          toast.error(response.message || 'Failed to update maintenance provider')
        }
      } else {
        const response = await maintenanceProviderMasterService.createProvider(formData)
        if (response.success) {
          toast.success('Maintenance provider created successfully!')
          navigate('/assets/maintenance-provider-master')
        } else {
          toast.error(response.message || 'Failed to create maintenance provider')
        }
      }
    } catch (error) {
      console.error('Error saving maintenance provider:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save maintenance provider')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadProviderData(recordId)
    } else {
      setFormData({ ...emptyForm, providerCode: formData.providerCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Maintenance Providers', path: '/assets/maintenance-provider-master' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Provider' : isViewMode ? 'View Provider' : 'New Provider'}</h1>
          <p>{isViewMode ? 'View provider details' : isEditMode ? 'Update provider information' : 'Create a new maintenance provider'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/maintenance-provider-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Maintenance Provider Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Basic Information</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Provider Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="providerCode"
                  value={formData.providerCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Provider Name <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="providerName"
                  value={formData.providerName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Specialty</label>
                <input
                  type="text"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="HVAC, IT Hardware, Electrical"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={fullWidthStyle}>
                <label style={labelStyle}>Description</label>
                <textarea
                  name="providerDescription"
                  value={formData.providerDescription}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Contact Details</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MaintenanceProviderMasterForm
